import { HolderFormProps } from "@/components/HolderForm/HolderFormProps.ts";
import { UserInfoFI } from "@/components/HolderForm/UserInfoFI.ts";
import { CountrySelect } from "@/components/Inputs/CountrySelect/CountrySelect.tsx";
import { FormFieldErrors } from "@/components/Inputs/FormInputErr/FormFieldErrors.tsx";
import {
  addressRuleset,
  companyRuleset,
  emailRuleset,
  nameAndSurnameRuleset,
  nameRuleset,
} from "@/helpers/form/FormRules.ts";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export const HolderForm = (props: HolderFormProps) => {
  const { t } = useTranslation();
  const { register, errors, setValue, watch, trigger } = props;
  const showCompanyFields = watch(UserInfoFI.IsCompany);
  const noteCharCount = watch(UserInfoFI.Note)?.length;

  // reset fields if checkbox unchecked
  useEffect(() => {
    if (!showCompanyFields) {
      setValue(UserInfoFI.CompanyName, "");
      setValue(UserInfoFI.CompanyAddress, "");
      setValue(UserInfoFI.CompanyVAT, "");
      setValue(UserInfoFI.CompanyCountry, "");
    }
  }, [showCompanyFields, setValue]);

  return (
    <div className="c-holder-form-wrapper">
      <form className="userInfoForm c-be-booking-form">
        <h3 className="u-h6 u-fw-600 c-be-booking-form__title">{t("resHolderInformation")}</h3>
        <div className="formFieldRow c-be-booking-form__field c-be-booking-form__field--checkbox">
          <input type="checkbox" {...register(UserInfoFI.IsCompany)} id={UserInfoFI.IsCompany} />
          <label htmlFor={UserInfoFI.IsCompany}>{t(UserInfoFI.IsCompany)}</label>
          <FormFieldErrors errors={errors} name={UserInfoFI.IsCompany} />
        </div>
        {showCompanyFields && (
          <>
            <h4 className="u-h6 u-fw-600 c-be-booking-form__subtitle">{t("companyInfo")}</h4>
            <div className="userInfoFormFieldsWrap c-be-booking-form__fields">
              <fieldset className="formField c-be-booking-form__field">
                <label htmlFor={UserInfoFI.CompanyName}>{t(UserInfoFI.CompanyName)} *</label>
                <input type="text" {...register(UserInfoFI.CompanyName, companyRuleset)} />
                <FormFieldErrors errors={errors} name={UserInfoFI.CompanyName} />
              </fieldset>
              <fieldset className="formField c-be-booking-form__field">
                <label htmlFor={UserInfoFI.CompanyAddress}>{t(UserInfoFI.CompanyAddress)} *</label>
                <input type="text" {...register(UserInfoFI.CompanyAddress, addressRuleset)} />
                <FormFieldErrors errors={errors} name={UserInfoFI.CompanyAddress} />
              </fieldset>
              <fieldset className="formField c-be-booking-form__field">
                <label htmlFor={UserInfoFI.CompanyVAT}>{t(UserInfoFI.CompanyVAT)} *</label>
                <input type="number" {...register(UserInfoFI.CompanyVAT, nameRuleset)} />
                <FormFieldErrors errors={errors} name={UserInfoFI.CompanyVAT} />
              </fieldset>
              <fieldset className="formField c-be-booking-form__field">
                <CountrySelect
                  register={register}
                  name={UserInfoFI.CompanyCountry}
                  setValue={setValue}
                  watch={watch}
                  isMandatory
                  trigger={trigger}
                />
                <FormFieldErrors errors={errors} name={UserInfoFI.CompanyCountry} />
              </fieldset>
            </div>
          </>
        )}
        {showCompanyFields && (
          <h4 className="u-h6 u-fw-600 c-be-booking-form__subtitle">{t("personalInfo")}</h4>
        )}
        <div className="userInfoFormFieldsWrap c-be-booking-form__fields">
          <fieldset className="formField c-be-booking-form__field">
            <label htmlFor={UserInfoFI.FirstName}>{t(UserInfoFI.FirstName)} *</label>
            <input type="text" {...register(UserInfoFI.FirstName, nameAndSurnameRuleset)} />
            <FormFieldErrors errors={errors} name={UserInfoFI.FirstName} />
          </fieldset>
          <fieldset className="formField c-be-booking-form__field">
            <label htmlFor={UserInfoFI.LastName}>{t(UserInfoFI.LastName)} *</label>
            <input type="text" {...register(UserInfoFI.LastName, nameAndSurnameRuleset)} />
            <FormFieldErrors errors={errors} name={UserInfoFI.LastName} />
          </fieldset>
          <fieldset className="formField c-be-booking-form__field">
            <CountrySelect
              register={register}
              setValue={setValue}
              watch={watch}
              isMandatory
              name={UserInfoFI.Country}
              trigger={trigger}
            />
            <FormFieldErrors errors={errors} name={UserInfoFI.Country} />
          </fieldset>
          <fieldset className="formField c-be-booking-form__field">
            <label htmlFor={UserInfoFI.Address}>{t(UserInfoFI.Address)} *</label>
            <input type="text" {...register(UserInfoFI.Address, addressRuleset)} />
            <FormFieldErrors errors={errors} name={UserInfoFI.Address} />
          </fieldset>
          <fieldset className="formField c-be-booking-form__field">
            <label htmlFor={UserInfoFI.PostalCode}>{t(UserInfoFI.PostalCode)} *</label>
            <input type="text" {...register(UserInfoFI.PostalCode, nameRuleset)} />
            <FormFieldErrors errors={errors} name={UserInfoFI.PostalCode} />
          </fieldset>
          <fieldset className="formField c-be-booking-form__field">
            <label htmlFor={UserInfoFI.City}>{t(UserInfoFI.City)} *</label>
            <input type="text" {...register(UserInfoFI.City, nameRuleset)} />
            <FormFieldErrors errors={errors} name={UserInfoFI.City} />
          </fieldset>
          <fieldset className="formField c-be-booking-form__field">
            <label htmlFor={UserInfoFI.Email}>{t(UserInfoFI.Email)} *</label>
            <input type="email" {...register(UserInfoFI.Email, emailRuleset(t("form.invalidEmailFormat")))} />
            <FormFieldErrors errors={errors} name={UserInfoFI.Email} />
          </fieldset>
          <fieldset className="formField c-be-booking-form__field">
            <label htmlFor={UserInfoFI.Phone}>{t(UserInfoFI.Phone)} *</label>
            <input type="tel" {...register(UserInfoFI.Phone, nameRuleset)} />
            <FormFieldErrors errors={errors} name={UserInfoFI.Phone} />
          </fieldset>
          <fieldset className="formField c-be-booking-form__field  c-be-booking-form__field--fullwidth">
            <label htmlFor={UserInfoFI.Note}>{t(UserInfoFI.Note)}</label>
            <textarea
              rows={5}
              maxLength={250}
              {...register(UserInfoFI.Note, { maxLength: { value: 250, message: "" } })}
            />
            <span className="noteCharCount">{noteCharCount} / 250</span>
            <FormFieldErrors errors={errors} name={UserInfoFI.Note} />
          </fieldset>
        </div>
      </form>
    </div>
  );
};
